import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const faqs = [
  {
    group: '网站使用',
    items: [
      { q: '如何在动漫之家漫画网快速找到想追的作品？', a: '你可以通过顶部搜索框直接输入作品名或作者名，也可以前往排行榜与分类大全页，按榜单、类型、完结状态筛选，几秒钟就能锁定心仪作品。' },
      { q: '动漫之家漫画网可以离线保存内容吗？', a: '本站以作品信息导航为主，暂不支持离线缓存。建议将常用分类页加入书签，方便下次快速访问。' },
      { q: '网站对移动端友好吗？', a: '网站采用响应式设计，兼容电脑、平板与手机，移动端可正常展开与收起菜单，浏览体验流畅。' },
    ],
  },
  {
    group: '内容与榜单',
    items: [
      { q: '排行榜热门推荐多久更新一次？', a: '热门推荐与排行榜数据综合近期的阅读热度、收藏量与讨论度持续滚动更新，让真正受读者欢迎的作品始终排在前面。' },
      { q: '完结漫画和连载中漫画有什么区别？', a: '完结漫画已更新至最终话，可以一口气追完不吊胃口；连载中作品则按章节持续更新，我们会在首页显示最新更新动态。' },
      { q: '汉化组独家分类内容从哪来？', a: '动漫之家漫画网与多个优秀汉化组合作，聚合其独家翻译与精校内容，并按热血、恋爱、悬疑等主题整理成独立分类，方便集中浏览。' },
    ],
  },
  {
    group: '合作与反馈',
    items: [
      { q: '汉化组或作者可以申请合作吗？', a: '当然可以。欢迎通过页面底部的联系邮箱或官方交流群与我们沟通，我们期待与优质内容团队建立合作，共同丰富分类大全。' },
      { q: '如何提交建议或反馈问题？', a: '你可以通过联系邮箱发送邮件，或在官方交流群中留言。我们会认真阅读每一条反馈，持续优化网站体验。' },
      { q: '发现信息有误该如何处理？', a: '如果发现作品名称、作者或分类信息有误，欢迎第一时间联系我们更正。感谢每一位帮助完善内容库的读者。' },
    ],
  },
];

export default function Faq() {
  useSeo({
    title: '动漫之家漫画网常见问题 - 使用帮助与解答',
    description:
      '动漫之家漫画网常见问题汇总，涵盖网站使用、排行榜、完结漫画、汉化组分类与内容反馈等使用帮助，解答你想知道的一切。',
    canonicalPath: '/faq',
  });

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  const allFaq = faqs.flatMap((f) => f.items);

  return (
    <div>
      {/* Head */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">常见问题</span>
          </nav>
          <div className="mt-4 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
              <i className="ri-question-line"></i> 常见问题
            </span>
            <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">使用前你想知道的都在这里</h1>
            <p className="mt-2 text-sm text-foreground-500">关于排行榜、完结漫画、汉化组分类与使用帮助的常见解答。</p>
          </div>
        </div>
      </section>

      {/* FAQ list */}
      <section className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-12" aria-label="常见问题列表">
        {faqs.map((group, gi) => (
          <div key={group.group} className={gi > 0 ? 'mt-10' : ''}>
            <h2 className="font-heading text-lg text-foreground-950">{group.group}</h2>
            <div className="mt-4 space-y-3">
              {group.items.map((item, ii) => {
                const key = `${gi}-${ii}`;
                const open = !!openMap[key];
                return (
                  <div key={key} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                      aria-expanded={open}
                    >
                      <span className="text-sm font-bold text-foreground-950">{item.q}</span>
                      <i className={`ri-add-line text-lg shrink-0 text-primary-500 transition-transform ${open ? 'rotate-45' : ''}`}></i>
                    </button>
                    {open && (
                      <p className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-10 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
          <h3 className="font-heading text-lg text-primary-700">没找到想要的答案？</h3>
          <p className="mt-2 text-sm text-foreground-600">欢迎随时联系我们，我们会尽快回复。</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <a href="mailto:service@dmzj-comic.example.com" className="whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
              <i className="ri-mail-line mr-1"></i> 联系邮箱
            </a>
            <Link to="/about" className="whitespace-nowrap rounded-lg border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100">
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* FAQPage JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: allFaq.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
    </div>
  );
}